import React, { useEffect, useState } from 'react';
import { SaveIcon, XIcon, CalendarIcon, BuildingIcon } from 'lucide-react';
import { Catalog, Entity, Vendor } from '../CatalogsManagement';
import { flattenOrgUnits, sampleOrgUnits, type OrgUnit } from '../../financial/sharedFinancialData';
interface CatalogFormProps {
  catalog: Catalog | null;
  vendors: Vendor[];
  entities: Entity[];
  onSave: (catalogData: Partial<Catalog>) => void;
  onCancel: () => void;
  locale: string;
}
export function CatalogForm({
  catalog,
  vendors,
  entities,
  onSave,
  onCancel,
  locale
}: CatalogFormProps) {
  // Form state
  const [formData, setFormData] = useState<Partial<Catalog>>({
    name: '',
    description: '',
    vendorId: '',
    activeFrom: new Date().toISOString().split('T')[0],
    activeTo: null,
    isActive: true,
    orgUnitIds: []
  });
  // Selected OrgUnits for UI
  const [selectedOrgUnits, setSelectedOrgUnits] = useState<OrgUnit[]>([]);
  // Get all OrgUnits from shared data
  const allOrgUnits = flattenOrgUnits(sampleOrgUnits);
  // Initialize form with catalog data if editing
  useEffect(() => {
    if (catalog) {
      setFormData({
        name: catalog.name,
        description: catalog.description,
        vendorId: catalog.vendorId,
        activeFrom: catalog.activeFrom,
        activeTo: catalog.activeTo,
        isActive: catalog.isActive,
        orgUnitIds: catalog.orgUnitIds || []
      });
      // Find selected OrgUnits
      const selectedUnits = allOrgUnits.filter(ou => catalog.orgUnitIds?.includes(ou.id));
      setSelectedOrgUnits(selectedUnits);
    }
  }, [catalog, allOrgUnits]);
  // Handle form field changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    // Handle checkbox
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
      return;
    }
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  // Handle OrgUnit selection
  const handleOrgUnitChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const options = e.target.options;
    const selectedValues: string[] = [];
    for (let i = 0; i < options.length; i++) {
      if (options[i].selected) {
        selectedValues.push(options[i].value);
      }
    }
    // Find the OrgUnit objects
    const selectedUnits = allOrgUnits.filter(ou => selectedValues.includes(ou.id));
    setSelectedOrgUnits(selectedUnits);
    // Update form data
    setFormData(prev => ({
      ...prev,
      orgUnitIds: selectedValues
    }));
  };
  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };
  return <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-13349">
      <div className="px-4 py-5 sm:px-6 flex justify-between items-center" data-id="element-13350">
        <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-13351">
          {catalog ? 'Edit Catalog' : 'Create Catalog'}
        </h3>
        <button onClick={onCancel} className="inline-flex items-center p-1.5 border border-transparent rounded-full shadow-sm text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-13352">
          <XIcon className="h-5 w-5" data-id="element-13353" />
        </button>
      </div>
      <form onSubmit={handleSubmit} data-id="element-13354">
        <div className="border-t border-gray-200 px-4 py-5 sm:p-6" data-id="element-13355">
          <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-13356">
            {/* Catalog Name */}
            <div className="sm:col-span-4" data-id="element-13357">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700" data-id="element-13358">
                Catalog Name
              </label>
              <div className="mt-1" data-id="element-13359">
                <input type="text" name="name" id="name" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={formData.name} onChange={handleChange} required data-id="element-13360" />
              </div>
            </div>

            {/* Vendor */}
            <div className="sm:col-span-3" data-id="element-13361">
              <label htmlFor="vendorId" className="block text-sm font-medium text-gray-700" data-id="element-13362">
                Vendor
              </label>
              <div className="mt-1" data-id="element-13363">
                <select id="vendorId" name="vendorId" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={formData.vendorId} onChange={handleChange} required data-id="element-13364">
                  <option value="" data-id="element-13365">Select Vendor</option>
                  {vendors.map(vendor => <option key={vendor.id} value={vendor.id} data-id="element-13366">
                      {vendor.name}
                    </option>)}
                </select>
              </div>
            </div>

            {/* Active Status */}
            <div className="sm:col-span-3 flex items-center" data-id="element-13367">
              <div className="flex h-5 items-center" data-id="element-13368">
                <input id="isActive" name="isActive" type="checkbox" className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" checked={formData.isActive} onChange={handleChange} data-id="element-13369" />
              </div>
              <div className="ml-3 text-sm" data-id="element-13370">
                <label htmlFor="isActive" className="font-medium text-gray-700" data-id="element-13371">
                  Active
                </label>
                <p className="text-gray-500" data-id="element-13372">
                  Make this catalog available for use
                </p>
              </div>
            </div>

            {/* Description */}
            <div className="sm:col-span-6" data-id="element-13373">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700" data-id="element-13374">
                Description
              </label>
              <div className="mt-1" data-id="element-13375">
                <textarea id="description" name="description" rows={3} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={formData.description} onChange={handleChange} data-id="element-13376" />
              </div>
              <p className="mt-2 text-sm text-gray-500" data-id="element-13377">
                Brief description of the catalog and its contents
              </p>
            </div>

            {/* Active From */}
            <div className="sm:col-span-3" data-id="element-13378">
              <label htmlFor="activeFrom" className="block text-sm font-medium text-gray-700" data-id="element-13379">
                Active From
              </label>
              <div className="mt-1 relative rounded-md shadow-sm" data-id="element-13380">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-13381">
                  <CalendarIcon className="h-5 w-5 text-gray-400" data-id="element-13382" />
                </div>
                <input type="date" name="activeFrom" id="activeFrom" className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" value={formData.activeFrom} onChange={handleChange} required data-id="element-13383" />
              </div>
            </div>

            {/* Active To */}
            <div className="sm:col-span-3" data-id="element-13384">
              <label htmlFor="activeTo" className="block text-sm font-medium text-gray-700" data-id="element-13385">
                Active To
              </label>
              <div className="mt-1 relative rounded-md shadow-sm" data-id="element-13386">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-13387">
                  <CalendarIcon className="h-5 w-5 text-gray-400" data-id="element-13388" />
                </div>
                <input type="date" name="activeTo" id="activeTo" className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" value={formData.activeTo || ''} onChange={handleChange} data-id="element-13389" />
              </div>
              <p className="mt-2 text-sm text-gray-500" data-id="element-13390">
                Leave blank for no end date
              </p>
            </div>

            {/* Organisation Units */}
            <div className="sm:col-span-6" data-id="element-13391">
              <label htmlFor="orgUnits" className="block text-sm font-medium text-gray-700 flex items-center" data-id="element-13392">
                <BuildingIcon className="h-4 w-4 mr-1" data-id="element-13393" />
                Organisation Units
              </label>
              <div className="mt-1" data-id="element-13394">
                <select id="orgUnits" name="orgUnits" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" multiple size={6} value={formData.orgUnitIds || []} onChange={handleOrgUnitChange} data-id="element-13395">
                  {allOrgUnits.map(orgUnit => <option key={orgUnit.id} value={orgUnit.id} data-id="element-13396">
                      {orgUnit.name} ({orgUnit.path})
                      {orgUnit.orgUnitType && ` - ${orgUnit.orgUnitType.name}`}
                    </option>)}
                </select>
              </div>
              <p className="mt-2 text-sm text-gray-500" data-id="element-13397">
                Select which organisation units can access this catalog. Hold
                Ctrl/Cmd to select multiple. Leave empty to make available to
                all.
              </p>
              {selectedOrgUnits.length > 0 && <div className="mt-2 flex flex-wrap gap-2" data-id="element-13398">
                  {selectedOrgUnits.map(orgUnit => <span key={orgUnit.id} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800" data-id="element-13399">
                      {orgUnit.name}
                    </span>)}
                </div>}
            </div>
          </div>
        </div>

        <div className="px-4 py-3 bg-gray-50 text-right sm:px-6 flex justify-end space-x-3" data-id="element-13400">
          <button type="button" className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" onClick={onCancel} data-id="element-13401">
            Cancel
          </button>
          <button type="submit" className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-13402">
            <SaveIcon className="h-4 w-4 mr-2" data-id="element-13403" />
            {catalog ? 'Update Catalog' : 'Create Catalog'}
          </button>
        </div>
      </form>
    </div>;
}